/**
 * Value Pool — Theme Preview (v3 rebrand)
 *
 * Self-contained route at /theme-preview. Does NOT touch existing components
 * or tokens. Demonstrates the proposed visual system for sign-off before we
 * roll the theme across the whole app.
 *
 * Palette (locked to user-supplied CSS + logo analysis):
 *   --vp-blue:    #024383   (Brand dark — body text, hero copy)
 *   --vp-teal:    #00A991   (Logo gradient start)
 *   --vp-green:   #76CB7A   (Logo gradient mid)
 *   --vp-orange:  #F89A3D   (Logo gradient end — CTAs, highlights)
 *   --vp-ink:     #1A1F2C   (Headings on light)
 *   --vp-body:    #333333   (Body copy)
 *   --vp-cream:   #FFFDF5   (Page bg)
 *
 * Type system: Poppins (UI/body) + Montserrat (display). H1/H2 UPPERCASE 800.
 * Character avatars: DiceBear "fun-emoji" style — flat, geometric, brand-safe.
 */
import React from "react";
import { Link } from "react-router-dom";
import { PORTRAIT } from "./Landing";

// ---- Inline tokens so this file is fully isolated from the live theme ----
const C = {
  blue: "#024383",
  teal: "#00A991",
  green: "#76CB7A",
  orange: "#F89A3D",
  ink: "#1A1F2C",
  body: "#333333",
  cream: "#FFFDF5",
  paper: "#FFFFFF",
  border: "rgba(2, 67, 131, 0.12)",
};

const fontDisplay = "'Montserrat', 'Poppins', system-ui, sans-serif";
const fontBody = "'Poppins', 'Montserrat', system-ui, sans-serif";

const Pill = ({ children, tone = "teal" }) => {
  const map = {
    teal: { bg: "rgba(0,169,145,.10)", fg: C.teal },
    orange: { bg: "rgba(248,154,61,.12)", fg: "#C46A14" },
    blue: { bg: "rgba(2,67,131,.08)", fg: C.blue },
  };
  const t = map[tone] || map.teal;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "5px 11px", borderRadius: 999, fontSize: 11, fontWeight: 700,
      letterSpacing: ".08em", textTransform: "uppercase",
      background: t.bg, color: t.fg, fontFamily: fontBody,
    }}>{children}</span>
  );
};

const PrimaryBtn = ({ children, ...p }) => (
  <button {...p} style={{
    fontFamily: fontDisplay, fontWeight: 800, fontSize: 13.5,
    textTransform: "uppercase", letterSpacing: ".06em",
    color: "#fff",
    background: `linear-gradient(135deg, ${C.orange} 0%, #E76B0E 100%)`,
    border: "none", borderRadius: 999, padding: "13px 26px",
    boxShadow: "0 10px 24px -10px rgba(231,107,14,.55)",
    cursor: "pointer", transition: "transform .15s ease, box-shadow .2s ease",
    ...(p.style || {}),
  }}
    onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
    onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
  >{children}</button>
);

const GhostBtn = ({ children, ...p }) => (
  <button {...p} style={{
    fontFamily: fontDisplay, fontWeight: 700, fontSize: 13.5,
    textTransform: "uppercase", letterSpacing: ".06em",
    color: C.blue, background: "transparent",
    border: `2px solid ${C.blue}`, borderRadius: 999, padding: "11px 22px",
    cursor: "pointer", transition: "background .15s ease, color .15s ease",
    ...(p.style || {}),
  }}
    onMouseEnter={(e) => { e.currentTarget.style.background = C.blue; e.currentTarget.style.color = "#fff"; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.blue; }}
  >{children}</button>
);

// ---- Realistic member portrait (same photos as the landing page) ----
const Avatar = ({ src, name, size = 72 }) => (
  <img
    alt={name ? `${name}, Value Pool member` : ""}
    src={src}
    width={size} height={size}
    loading="lazy"
    style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", display: "block", boxShadow: "0 6px 16px -6px rgba(2,67,131,.35)", background: "#fff" }}
  />
);

// ---- Section helpers ----
const Section = ({ children, bg, id }) => (
  <section id={id} style={{ background: bg || "transparent", padding: "72px 24px" }}>
    <div style={{ maxWidth: 1160, margin: "0 auto" }}>{children}</div>
  </section>
);

const H2 = ({ children, align = "left" }) => (
  <h2 style={{
    fontFamily: fontDisplay, fontWeight: 800, fontSize: "clamp(28px,4vw,42px)",
    color: C.ink, textTransform: "uppercase", letterSpacing: "-0.02em",
    margin: 0, textAlign: align, lineHeight: 1.05,
  }}>{children}</h2>
);

// ===========================================================================
export default function ThemePreview() {
  return (
    <div style={{
      fontFamily: fontBody, color: C.blue, background: C.cream, minHeight: "100vh",
    }} data-testid="theme-preview">

      {/* Banner */}
      <div style={{
        background: `linear-gradient(90deg, ${C.teal}, ${C.green}, ${C.orange})`,
        color: "#fff", fontFamily: fontDisplay, fontWeight: 700, fontSize: 12,
        textTransform: "uppercase", letterSpacing: ".12em",
        padding: "9px 16px", textAlign: "center",
      }}>
        ✨ Theme Preview · v3 · For approval — does not affect live app
      </div>

      {/* Header */}
      <header style={{
        position: "sticky", top: 0, zIndex: 30, background: "rgba(255,253,245,.92)",
        backdropFilter: "blur(10px)",
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{
          maxWidth: 1160, margin: "0 auto", display: "flex",
          alignItems: "center", padding: "14px 24px", gap: 14,
        }}>
          <img src="/valuepool-logo-v3.png" alt="Value Pool" style={{ height: 56, width: "auto" }} />
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
            <span style={{ fontFamily: fontDisplay, fontWeight: 900, fontSize: 22, color: C.blue, letterSpacing: "-.02em" }}>
              Value Pool
            </span>
            <span style={{ fontFamily: fontBody, fontWeight: 600, fontSize: 11, color: C.orange, textTransform: "uppercase", letterSpacing: ".14em" }}>
              Pool of Smart Savings
            </span>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 20, alignItems: "center" }}>
            {["How it works", "Rewards", "Partners", "Sales"].map((t) => (
              <a key={t} href="#"
                style={{
                  fontFamily: fontBody, fontSize: 14, fontWeight: 500,
                  color: C.blue, textDecoration: "none",
                }}
              >{t}</a>
            ))}
            <GhostBtn>Log in</GhostBtn>
            <PrimaryBtn>Get rewarded</PrimaryBtn>
          </div>
        </div>
      </header>

      {/* HERO ================================================================ */}
      <Section bg={C.cream}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "1.15fr 1fr",
          gap: 56, alignItems: "center",
        }}>
          <div>
            <Pill tone="orange">🎁 New · Monthly rewards live</Pill>
            <h1 style={{
              fontFamily: fontDisplay, fontWeight: 900, color: C.ink,
              fontSize: "clamp(40px,6.4vw,76px)", lineHeight: .98,
              letterSpacing: "-.025em", textTransform: "uppercase",
              margin: "18px 0 22px",
            }}>
              Unlock<br />
              <span style={{
                background: `linear-gradient(90deg, ${C.teal} 0%, ${C.green} 45%, ${C.orange} 100%)`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>Free Rewards</span><br />
              with Every Bill.
            </h1>
            <p style={{
              fontFamily: fontBody, fontSize: 17, fontWeight: 400, lineHeight: 1.55,
              color: C.body, maxWidth: 520, margin: "0 0 30px",
            }}>
              One pass → instant discounts at hostels, PGs, cafés, restaurants
              and snack shops near you. Show your dynamic QR, the partner scans,
              the savings drop straight into your pocket.
            </p>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <PrimaryBtn>Start saving free →</PrimaryBtn>
              <GhostBtn>I'm a business · Become a partner</GhostBtn>
            </div>
            <div style={{ display: "flex", gap: 24, marginTop: 40, flexWrap: "wrap" }}>
              {[
                { k: "₹2.4 Cr+", v: "Saved by members" },
                { k: "1,200+", v: "Partner outlets" },
                { k: "4.8 / 5", v: "Member rating" },
              ].map((s) => (
                <div key={s.k}>
                  <div style={{ fontFamily: fontDisplay, fontWeight: 800, fontSize: 26, color: C.blue, letterSpacing: "-.01em" }}>{s.k}</div>
                  <div style={{ fontFamily: fontBody, fontSize: 12, fontWeight: 600, color: C.teal, textTransform: "uppercase", letterSpacing: ".1em" }}>{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero visual */}
          <div style={{ position: "relative", display: "flex", justifyContent: "center" }}>
            <div style={{
              width: "100%", maxWidth: 440, aspectRatio: "1/1",
              background: `radial-gradient(circle at 50% 40%, rgba(0,169,145,.18) 0%, rgba(118,203,122,.10) 40%, rgba(255,253,245,0) 70%)`,
              borderRadius: "50%", display: "grid", placeItems: "center",
              position: "relative",
            }}>
              <img src="/valuepool-logo-v3.png" alt="" style={{ width: "82%", filter: "drop-shadow(0 30px 60px rgba(2,67,131,.18))" }} />
              {/* Floating chips */}
              <div style={{ position: "absolute", top: 14, right: 8, transform: "rotate(6deg)" }}>
                <Card mini emoji="💰" label="15% off" sub="Hostels" />
              </div>
              <div style={{ position: "absolute", bottom: 30, left: -8, transform: "rotate(-4deg)" }}>
                <Card mini emoji="☕" label="Free latte" sub="Welcome perk" />
              </div>
              <div style={{ position: "absolute", bottom: -10, right: 30, transform: "rotate(3deg)" }}>
                <Card mini emoji="🎉" label="Referral" sub="₹100 credit" />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* HOW IT WORKS ======================================================= */}
      <Section bg={C.paper}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <Pill tone="teal">Three steps</Pill>
          <div style={{ marginTop: 12 }}><H2 align="center">How Value Pool works</H2></div>
          <p style={{ fontFamily: fontBody, color: C.body, maxWidth: 560, margin: "16px auto 0", fontSize: 16 }}>
            No coupon hunting. No app-store deals. Pay your bill, save instantly.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
          {[
            { n: "01", title: "Sign up with your phone", body: "Verify your mobile via OTP. Pick a monthly pass. No paperwork.", img: PORTRAIT.priyaStudent, who: "Priya", emoji: "📱" },
            { n: "02", title: "Show your dynamic QR", body: "At any partner — café, PG, hostel — flash the QR from your dashboard. Refreshes every 5 minutes.", img: PORTRAIT.aaravTraveler, who: "Aarav", emoji: "🔐" },
            { n: "03", title: "Save on the bill", body: "Partner scans, enters bill, discount + complementary perks auto-apply. Rate & repeat.", img: PORTRAIT.ishaDesigner, who: "Isha", emoji: "🎁" },
          ].map((s) => (
            <article key={s.n} style={{
              background: C.cream, borderRadius: 24, padding: 28,
              border: `1.5px solid ${C.border}`, position: "relative",
              transition: "transform .2s ease, box-shadow .2s ease",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
                <Avatar src={s.img} name={s.who} size={56} />
                <div style={{
                  fontFamily: fontDisplay, fontWeight: 900, fontSize: 38,
                  background: `linear-gradient(135deg, ${C.teal}, ${C.orange})`,
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                  marginLeft: "auto", letterSpacing: "-.04em",
                }}>{s.n}</div>
              </div>
              <h3 style={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: 19, color: C.blue, margin: "0 0 8px" }}>
                {s.title}
              </h3>
              <p style={{ fontFamily: fontBody, fontSize: 14.5, color: C.body, lineHeight: 1.6, margin: 0 }}>
                {s.body}
              </p>
            </article>
          ))}
        </div>
      </Section>

      {/* FEATURE CARDS ======================================================= */}
      <Section bg={C.cream}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}>
          {[
            { emoji: "📍", title: "Near-me discovery", body: "Geo-locate partners around you in seconds." },
            { emoji: "🎟️", title: "Complementary perks", body: "Free drinks, desserts, welcome treats — on top of discounts." },
            { emoji: "♻️", title: "Smart cycle limits", body: "Fair use built in — caps reset on renewal." },
            { emoji: "🛡️", title: "Verified guest badge", body: "KYC-approved members get priority treatment." },
          ].map((f) => (
            <div key={f.title} style={{
              background: "#fff", borderRadius: 20, padding: 22,
              border: `1.5px solid ${C.border}`,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14, display: "grid", placeItems: "center",
                background: `linear-gradient(135deg, ${C.teal}, ${C.green})`, fontSize: 24,
                marginBottom: 14,
              }}>{f.emoji}</div>
              <div style={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: 15.5, color: C.blue, marginBottom: 6 }}>
                {f.title}
              </div>
              <div style={{ fontFamily: fontBody, fontSize: 13.5, color: C.body, lineHeight: 1.5 }}>
                {f.body}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* TESTIMONIALS w/ character avatars =================================== */}
      <Section bg={C.paper}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <Pill tone="orange">Voices from the pool</Pill>
          <div style={{ marginTop: 12 }}><H2 align="center">Real members. Real savings.</H2></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 22 }}>
          {[
            { name: "Aarav M.", role: "Bangalore · Student", img: PORTRAIT.maleStudent, quote: "Paid for my PG with the money I saved on lunch. Genuinely — Value Pool pays for itself in two outings." },
            { name: "Isha I.", role: "Mumbai · Designer", img: PORTRAIT.femalePro, quote: "Show QR, save 15%, get a free dessert. It's basically gamified eating out." },
            { name: "Meera N.", role: "Pune · Sales", img: PORTRAIT.femaleTraveller, quote: "I travel for work. The hostel discounts alone covered my plan in week one." },
          ].map((t) => (
            <figure key={t.name} style={{
              margin: 0, background: C.cream, padding: 26, borderRadius: 22,
              border: `1.5px solid ${C.border}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <Avatar src={t.img} name={t.name} size={48} />
                <div>
                  <div style={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: 14, color: C.blue }}>{t.name}</div>
                  <div style={{ fontFamily: fontBody, fontSize: 12, color: C.teal, fontWeight: 600 }}>{t.role}</div>
                </div>
                <div style={{ marginLeft: "auto", color: C.orange, fontSize: 14, letterSpacing: 2 }}>★★★★★</div>
              </div>
              <blockquote style={{ margin: 0, fontFamily: fontBody, fontSize: 14.5, lineHeight: 1.6, color: C.body }}>
                "{t.quote}"
              </blockquote>
            </figure>
          ))}
        </div>
      </Section>

      {/* STYLE GUIDE PANEL ================================================== */}
      <Section bg={C.cream}>
        <Pill tone="blue">Style guide</Pill>
        <div style={{ marginTop: 10, marginBottom: 28 }}><H2>Tokens at a glance</H2></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>
          <div>
            <div style={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: ".14em", color: C.teal, marginBottom: 10 }}>Colour</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              {[
                ["Brand Blue", C.blue], ["Teal", C.teal], ["Green", C.green], ["Orange", C.orange],
                ["Ink", C.ink], ["Body", C.body], ["Cream", C.cream], ["Paper", C.paper],
              ].map(([n, c]) => (
                <div key={n}>
                  <div style={{ background: c, height: 56, borderRadius: 12, border: `1px solid ${C.border}` }} />
                  <div style={{ fontFamily: fontBody, fontSize: 11, fontWeight: 600, color: C.blue, marginTop: 6 }}>{n}</div>
                  <div style={{ fontFamily: "ui-monospace,monospace", fontSize: 10.5, color: C.body }}>{c}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: ".14em", color: C.teal, marginBottom: 10 }}>Type</div>
            <div style={{ background: "#fff", padding: 18, borderRadius: 14, border: `1.5px solid ${C.border}` }}>
              <div style={{ fontFamily: fontDisplay, fontWeight: 900, fontSize: 30, color: C.ink, textTransform: "uppercase", letterSpacing: "-.02em", lineHeight: 1 }}>
                Display · Montserrat 900
              </div>
              <div style={{ fontFamily: fontDisplay, fontWeight: 800, fontSize: 18, color: C.blue, marginTop: 14, textTransform: "uppercase" }}>
                H2 · Montserrat 800 uppercase
              </div>
              <div style={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: 15, color: C.blue, marginTop: 10 }}>
                H3 · Montserrat 700 sentence
              </div>
              <div style={{ fontFamily: fontBody, fontSize: 14.5, color: C.body, marginTop: 14, lineHeight: 1.55 }}>
                Body · Poppins 400 — the quick brown fox jumps over the lazy
                dog, then claims its complementary latte at the Cozy Cafe.
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
                <PrimaryBtn>Primary</PrimaryBtn>
                <GhostBtn>Secondary</GhostBtn>
                <Pill>Default chip</Pill>
                <Pill tone="orange">Reward chip</Pill>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA banner ========================================================== */}
      <Section bg={C.paper}>
        <div style={{
          borderRadius: 28, padding: "48px 40px",
          background: `linear-gradient(135deg, ${C.blue} 0%, #0A6CB8 100%)`,
          color: "#fff", display: "grid",
          gridTemplateColumns: "1.4fr 1fr", gap: 32, alignItems: "center",
          position: "relative", overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", inset: 0, opacity: .22, pointerEvents: "none",
            background: `radial-gradient(circle at 90% 20%, ${C.orange} 0%, transparent 35%), radial-gradient(circle at 10% 90%, ${C.teal} 0%, transparent 35%)`,
          }} />
          <div style={{ position: "relative" }}>
            <h2 style={{
              fontFamily: fontDisplay, fontWeight: 900, fontSize: "clamp(28px,4vw,44px)",
              textTransform: "uppercase", letterSpacing: "-.02em", margin: 0, lineHeight: 1.02,
            }}>
              Stop hunting<br />coupons. <span style={{ color: C.orange }}>Start pooling.</span>
            </h2>
            <p style={{ fontFamily: fontBody, fontSize: 15.5, marginTop: 14, maxWidth: 460, color: "rgba(255,255,255,.85)" }}>
              Join 12,000+ smart savers and partner businesses across India.
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 24, flexWrap: "wrap" }}>
              <PrimaryBtn>Get started · free trial</PrimaryBtn>
              <button style={{
                fontFamily: fontDisplay, fontWeight: 700, fontSize: 13.5,
                textTransform: "uppercase", letterSpacing: ".06em",
                color: "#fff", background: "transparent",
                border: "2px solid rgba(255,255,255,.5)", borderRadius: 999,
                padding: "11px 22px", cursor: "pointer",
              }}>I'm a business</button>
            </div>
          </div>
          <div style={{ position: "relative", display: "flex", justifyContent: "center" }}>
            <div style={{
              background: "rgba(255,255,255,.10)", borderRadius: 24, padding: 18,
              backdropFilter: "blur(6px)", border: "1px solid rgba(255,255,255,.15)",
            }}>
              <Avatar src={PORTRAIT.maleTraveller} name="Vikram" size={120} />
            </div>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer style={{
        background: C.ink, color: "rgba(255,255,255,.75)",
        padding: "36px 24px", marginTop: 24,
      }}>
        <div style={{ maxWidth: 1160, margin: "0 auto", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <img src="/valuepool-logo-v3.png" alt="Value Pool" style={{ height: 36, filter: "brightness(1.1)" }} />
          <span style={{ fontFamily: fontDisplay, fontWeight: 800, color: "#fff", letterSpacing: "-.02em" }}>Value Pool</span>
          <span style={{ fontFamily: fontBody, fontSize: 12, color: "rgba(255,255,255,.55)", marginLeft: 12 }}>Pool of Smart Savings · v3 preview</span>
          <span style={{ marginLeft: "auto", fontFamily: fontBody, fontSize: 12 }}>
            <Link to="/" style={{ color: "rgba(255,255,255,.85)", textDecoration: "underline" }}>← Back to live app</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}

// Floating mini card used in hero composition
const Card = ({ mini, emoji, label, sub }) => (
  <div style={{
    background: "#fff", borderRadius: 16, padding: mini ? "10px 14px" : "14px 18px",
    boxShadow: "0 18px 40px -16px rgba(2,67,131,.22), 0 2px 6px rgba(2,67,131,.06)",
    border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10,
    minWidth: 140,
  }}>
    <div style={{
      width: 36, height: 36, borderRadius: 10,
      background: `linear-gradient(135deg, ${C.teal}, ${C.green})`,
      display: "grid", placeItems: "center", fontSize: 18,
    }}>{emoji}</div>
    <div style={{ lineHeight: 1.15 }}>
      <div style={{ fontFamily: fontDisplay, fontWeight: 800, fontSize: 14, color: C.blue }}>{label}</div>
      <div style={{ fontFamily: fontBody, fontSize: 11, color: C.body, fontWeight: 500 }}>{sub}</div>
    </div>
  </div>
);
